// Role-aware uvodni stranka. Misto stub karet ukazuje:
//  1) "Ceka na vase schvaleni" — pro SCHVALOVATEL/ADMIN, primarni akce
//  2) "Dnesni operativa" — pro vsechny, status + rychly link
//  3) "Tento tyden" — grid Po-Pa, kazdy den se stavem (nebo "zadna")
//  4) "Rychle odkazy" — gated tlacitka dle role
//
// Zadny novy backend. Vse jde pres existujici endpointy:
//   GET /api/schvaleni/cekajici
//   GET /api/operativy/by-date/{datum}
//   GET /api/operativy?od=...&doDate=...
//
// Sekce se renderuji jen kdyz davaji smysl pro role aktualniho uzivatele
// (pouzivame `hasRole` na presnou roli, ne `isAdmin` jako bypass — admin bez
// SCHVALOVATEL role schvalovaci sekci NEVIDI).

import { useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  ArrowRight,
  CalendarDays,
  CheckCircle2,
  ClipboardList,
  FileText,
  Loader2,
  Plus,
  ShieldCheck,
  Wrench,
} from 'lucide-react';

import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/lib/AuthContext';
import { usePermissions } from '@/lib/permissions';
import {
  DAY_LABELS_CS,
  formatWeekRangeCs,
  isoDate,
  startOfIsoWeek,
} from '@/lib/dateWeek';
import { formatDateCs, formatDateTimeCs } from '@/lib/utils';
import type {
  OperativaDetail,
  OperativaListItem,
  SchvaleniListItem,
  StavOperativy,
} from '@/types/api';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { StavBadge } from '@/components/StavBadge';

/** Lokalni "dnes" jako Date (cas vynulovany). useMemo aby reference byla stabilni v ramci renderu. */
function useToday(): Date {
  return useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);
}

export function DashboardPage() {
  const { user } = useAuth();
  const perms = usePermissions();

  // Schvalovaci sekce vidi SCHVALOVATEL nebo ADMIN se schvalovaci roli.
  // Per zadani: "Pokud isAdmin ale ADMIN nema SCHVALOVATEL roli, sekce 1 by se
  // nemela zobrazit. Pouzij presny role check." — tedy striktne podle SCHVALOVATEL
  // role; ADMIN obvykle ma vsechny role, ale pokud nahodou ne, sekci skryjeme.
  // TECHNIK_INT muze byt zastupcem schvalovatele — sekci vidi jen tehdy, kdyz
  // na nej (jako zastupce) skutecne neco ceka; jinak by mel trvale prazdnou kartu.
  const showSchvalovaciSekce = perms.isSchvalovatel || perms.isTechnikInt;
  const schvalovaciSekceJenKdyzCeka = !perms.isSchvalovatel;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-semibold">Vítejte, {user?.jmeno ?? '—'}.</h2>
        <p className="text-sm text-muted-foreground">
          Přehled denní operativy montáží a akcí, které čekají na vás.
        </p>
      </div>

      {showSchvalovaciSekce && <CekajiciSchvaleniSekce jenKdyzCeka={schvalovaciSekceJenKdyzCeka} />}

      <DnesniOperativaSekce />

      <TydenSekce />

      <RychleOdkazySekce
        canVytvoritOperativu={perms.canVytvoritOperativu}
        showProvozni={perms.isVedouci || perms.isAdmin || perms.isSchvalovatel}
        isAdmin={perms.isAdmin}
      />
    </div>
  );
}

// ============================================================================
// Sekce 1: Cekajici schvaleni (TOP priority pro SCHVALOVATEL/ADMIN)
// ============================================================================

function CekajiciSchvaleniSekce({ jenKdyzCeka }: { jenKdyzCeka: boolean }) {
  const query = useQuery<SchvaleniListItem[]>({
    queryKey: ['schvaleni', 'cekajici'],
    queryFn: () => api<SchvaleniListItem[]>('/schvaleni/cekajici'),
  });

  const items = query.data ?? [];
  const hasItems = items.length > 0;

  // Zastupce bez vlastni schvalovaci role: karta jen tehdy, kdyz na nej neco ceka.
  if (jenKdyzCeka && !hasItems) {
    return null;
  }

  // Pokud cekaji polozky, ozdobime kartu zlutym ramečkem aby byla videt prvni.
  const cardClass = hasItems
    ? 'border-amber-300 bg-amber-50/50'
    : '';

  return (
    <Card className={cardClass}>
      <CardHeader>
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <CardTitle className="flex items-center gap-2">
              <ShieldCheck className="h-5 w-5 text-amber-700" />
              Čeká na vaše schválení
            </CardTitle>
            <CardDescription>
              Operativy, u kterých jste vy (nebo někdo, koho zastupujete) podepsán jako schvalovatel.
            </CardDescription>
          </div>
          {hasItems && (
            <div className="rounded-full bg-amber-100 px-3 py-1 text-sm font-semibold text-amber-900">
              {items.length}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent>
        {query.isLoading ? (
          <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Načítám…
          </div>
        ) : query.isError ? (
          <p className="text-sm text-muted-foreground">
            Seznam čekajících schválení se nepodařilo načíst.
          </p>
        ) : !hasItems ? (
          <p className="flex items-center gap-2 text-sm text-muted-foreground">
            <CheckCircle2 className="h-4 w-4 text-emerald-600" />
            Žádné operativy nečekají na vaše schválení.
          </p>
        ) : (
          <div className="divide-y rounded-md border bg-card">
            {items.map((it) => (
              <CekajiciRadek key={it.id} item={it} />
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function CekajiciRadek({ item }: { item: SchvaleniListItem }) {
  return (
    <div className="grid grid-cols-12 items-center gap-3 px-3 py-2 text-sm">
      <div className="col-span-4 md:col-span-3">
        <div className="font-medium">{formatDateCs(item.operativaDatum)}</div>
        <div className="text-xs text-muted-foreground">Datum operativy</div>
      </div>
      <div className="col-span-4 md:col-span-5 text-muted-foreground">
        <div>
          Schvalovatel: <span className="text-foreground">{item.schvalovatelJmeno}</span>
        </div>
        <div className="text-xs">Vytvořeno {formatDateTimeCs(item.vytvoreno)}</div>
      </div>
      <div className="col-span-4 md:col-span-4 flex justify-end">
        <Button asChild size="sm">
          <Link to={`/operativy/${item.operativaId}`}>
            Otevřít
            <ArrowRight className="ml-1 h-4 w-4" />
          </Link>
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// Sekce 2: Dnesni operativa
// ============================================================================

function DnesniOperativaSekce() {
  const today = useToday();
  const todayIso = isoDate(today);
  const perms = usePermissions();
  const navigate = useNavigate();

  const query = useQuery<OperativaDetail | null>({
    queryKey: ['operativy', 'by-date', todayIso],
    // 404 je ocekavany stav (operativa pro dnesek nemusi existovat) — nechcem retry-bouchat.
    retry: false,
    queryFn: async () => {
      try {
        return await api<OperativaDetail>(`/operativy/by-date/${todayIso}`);
      } catch (e) {
        if (e instanceof ApiError && e.status === 404) return null;
        throw e;
      }
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <CalendarDays className="h-5 w-5 text-muted-foreground" />
          Dnešní operativa
        </CardTitle>
        <CardDescription>{formatDateCs(today)}</CardDescription>
      </CardHeader>
      <CardContent>
        {query.isLoading ? (
          <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Načítám…
          </div>
        ) : query.isError ? (
          <p className="text-sm text-muted-foreground">
            Stav dnešní operativy se nepodařilo načíst.
          </p>
        ) : !query.data ? (
          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-muted-foreground">
              Pro dnešek ({formatDateCs(today)}) ještě není operativa vytvořena.
            </p>
            {perms.canVytvoritOperativu && (
              <Button size="sm" onClick={() => navigate('/operativy')}>
                <Plus className="mr-1 h-4 w-4" />
                Nová operativa
              </Button>
            )}
          </div>
        ) : (
          <DnesniOperativaDetail detail={query.data} />
        )}
      </CardContent>
    </Card>
  );
}

function DnesniOperativaDetail({ detail }: { detail: OperativaDetail }) {
  const perms = usePermissions();
  const reportStavy: StavOperativy[] = ['SCHVALENO', 'V_REALIZACI', 'UPRAVENA'];
  const inReportStav = reportStavy.includes(detail.stav);

  // CTA dle role: vedouci/technik_ext zapisuji report, technik_int/schvalovatel kontroluji.
  const showReportCta =
    inReportStav && (perms.isVedouci || perms.isTechnikExt);
  const showKontrolaCta =
    inReportStav && perms.isInterni && (perms.isTechnikInt || perms.isSchvalovatel);

  return (
    <div className="flex flex-wrap items-center justify-between gap-4">
      <div className="space-y-1">
        <div className="flex items-center gap-2">
          <StavBadge stav={detail.stav} type="operativa" />
          <span className="text-sm text-muted-foreground">
            {detail.polozky.length}{' '}
            {detail.polozky.length === 1
              ? 'položka'
              : detail.polozky.length >= 2 && detail.polozky.length <= 4
                ? 'položky'
                : 'položek'}
          </span>
        </div>
        <div className="text-xs text-muted-foreground">
          Vytvořil {detail.vytvorilJmeno} · {formatDateTimeCs(detail.vytvoreno)}
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {showReportCta && (
          <Button asChild size="sm">
            <Link to={`/operativy/${detail.id}`}>
              <FileText className="mr-1 h-4 w-4" />
              Vyplnit report
            </Link>
          </Button>
        )}
        {showKontrolaCta && (
          <Button asChild size="sm" variant={showReportCta ? 'outline' : 'default'}>
            <Link to={`/operativy/${detail.id}`}>
              <ShieldCheck className="mr-1 h-4 w-4" />
              Zapsat kontrolu
            </Link>
          </Button>
        )}
        <Button asChild size="sm" variant={showReportCta || showKontrolaCta ? 'ghost' : 'default'}>
          <Link to={`/operativy/${detail.id}`}>
            Otevřít detail
            <ArrowRight className="ml-1 h-4 w-4" />
          </Link>
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// Sekce 3: Tento tyden (Po-Pa grid)
// ============================================================================

function TydenSekce() {
  const today = useToday();
  const monday = useMemo(() => startOfIsoWeek(today), [today]);
  const friday = useMemo(() => {
    const d = new Date(monday);
    d.setDate(d.getDate() + 4);
    return d;
  }, [monday]);

  const od = isoDate(monday);
  const doDate = isoDate(friday);

  const query = useQuery<OperativaListItem[]>({
    queryKey: ['operativy', 'week', od, doDate],
    queryFn: () =>
      api<OperativaListItem[]>(
        `/operativy?od=${encodeURIComponent(od)}&doDate=${encodeURIComponent(doDate)}`,
      ),
  });

  // Sestav 5 sloupcu Po-Pa a navaz operativu (kdyz existuje pro dany den).
  const days = useMemo(() => {
    const byDate = new Map<string, OperativaListItem>();
    for (const it of query.data ?? []) byDate.set(it.datum, it);

    return Array.from({ length: 5 }, (_, i) => {
      const d = new Date(monday);
      d.setDate(d.getDate() + i);
      const iso = isoDate(d);
      return {
        date: d,
        iso,
        label: DAY_LABELS_CS[i],
        operativa: byDate.get(iso) ?? null,
      };
    });
  }, [monday, query.data]);

  const todayIso = isoDate(today);

  // Soucty per-stav pro radek pod gridem.
  const counts = useMemo(() => {
    const c: Partial<Record<StavOperativy, number>> = {};
    for (const it of query.data ?? []) {
      c[it.stav] = (c[it.stav] ?? 0) + 1;
    }
    return c;
  }, [query.data]);

  const summaryParts: string[] = [];
  if (counts.NAVRH) summaryParts.push(`${counts.NAVRH} v návrhu`);
  if (counts.KE_SCHVALENI) summaryParts.push(`${counts.KE_SCHVALENI} ke schválení`);
  if (counts.SCHVALENO) summaryParts.push(`${counts.SCHVALENO} schválených`);
  if (counts.V_REALIZACI) summaryParts.push(`${counts.V_REALIZACI} v realizaci`);
  if (counts.UPRAVENA) summaryParts.push(`${counts.UPRAVENA} upravených`);
  if (counts.UZAVRENO) summaryParts.push(`${counts.UZAVRENO} uzavřených`);

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div className="space-y-1">
            <CardTitle>Tento týden</CardTitle>
            <CardDescription>{formatWeekRangeCs(monday)}</CardDescription>
          </div>
          <Button asChild variant="ghost" size="sm">
            <Link to="/operativy">
              Otevřít přehled
              <ArrowRight className="ml-1 h-4 w-4" />
            </Link>
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {query.isLoading ? (
          <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Načítám…
          </div>
        ) : query.isError ? (
          <p className="text-sm text-muted-foreground">
            Týdenní přehled se nepodařilo načíst.
          </p>
        ) : (
          <>
            <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-5">
              {days.map((d) => (
                <DayTile
                  key={d.iso}
                  label={d.label}
                  date={d.date}
                  iso={d.iso}
                  operativa={d.operativa}
                  isToday={d.iso === todayIso}
                />
              ))}
            </div>
            {summaryParts.length > 0 && (
              <p className="mt-4 text-xs text-muted-foreground">
                {summaryParts.join(' · ')}
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function DayTile({
  label,
  date,
  iso,
  operativa,
  isToday,
}: {
  label: string;
  date: Date;
  iso: string;
  operativa: OperativaListItem | null;
  isToday: boolean;
}) {
  const dateLabel = `${date.getDate()}. ${date.getMonth() + 1}.`;

  // Klik na den otevre detail operativy, pokud existuje. Jinak jen prehled
  // (kde si user na ten den muze nakliknout vytvoreni).
  const href = operativa ? `/operativy/${operativa.id}` : '/operativy';

  const base =
    'flex flex-col gap-2 rounded-md border p-3 text-sm transition-colors hover:bg-accent/50';
  const todayHighlight = isToday ? 'border-primary/50 ring-1 ring-primary/30' : '';

  return (
    <Link to={href} className={`${base} ${todayHighlight}`} aria-label={`${label} ${iso}`}>
      <div className="flex items-baseline justify-between gap-2">
        <span className={`font-medium ${isToday ? 'text-primary' : ''}`}>{label}</span>
        <span className="text-xs text-muted-foreground">{dateLabel}</span>
      </div>
      {operativa ? (
        <div className="flex flex-wrap items-center justify-between gap-1">
          <StavBadge stav={operativa.stav} type="operativa" />
          <span className="text-xs text-muted-foreground tabular-nums">
            {operativa.pocetPolozek} pol.
          </span>
        </div>
      ) : (
        <span className="text-xs text-muted-foreground">žádná</span>
      )}
    </Link>
  );
}

// ============================================================================
// Sekce 4: Rychle odkazy
// ============================================================================

function RychleOdkazySekce({
  canVytvoritOperativu,
  showProvozni,
  isAdmin,
}: {
  canVytvoritOperativu: boolean;
  showProvozni: boolean;
  isAdmin: boolean;
}) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Rychlé odkazy</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-4">
          {canVytvoritOperativu && (
            <RychlyOdkaz to="/operativy" icon={<Plus className="h-4 w-4" />} label="Nová operativa" />
          )}
          {showProvozni && (
            <RychlyOdkaz to="/mechanizace" icon={<Wrench className="h-4 w-4" />} label="Mechanizace" />
          )}
          {showProvozni && (
            <RychlyOdkaz
              to="/cinnosti"
              icon={<ClipboardList className="h-4 w-4" />}
              label="Činnosti"
            />
          )}
          {isAdmin && (
            <RychlyOdkaz to="/audit" icon={<FileText className="h-4 w-4" />} label="Audit log" />
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function RychlyOdkaz({
  to,
  icon,
  label,
}: {
  to: string;
  icon: React.ReactNode;
  label: string;
}) {
  return (
    <Button asChild variant="outline" className="h-auto justify-start py-3">
      <Link to={to} className="flex items-center gap-2">
        {icon}
        <span>{label}</span>
        <ArrowRight className="ml-auto h-4 w-4 text-muted-foreground" />
      </Link>
    </Button>
  );
}
